// src/features/chat/templates/futureActionCommitmentTemplates.js
/*
 * Templates for future-action commitments: durable proof that Affin accepted
 * a scheduled responsibility. Copy is rendered from the visible commitment
 * projection only; backend intent, occurrence, and handle state stays hidden.
 */

import { coerceTimestampMs } from '../models/hisTimelineModel';

const FUTURE_ACTION_COMMITMENT_SCHEMA_VERSION =
  'assistant-future-action-commitment-v1';
const FUTURE_ACTION_COMMITMENT_TYPE = 'future_action';
const SETTLED_OUTCOMES = new Set(['fulfilled', 'cancelled', 'failed']);

function hasCanonicalEnvelope(commitment) {
  return (
    commitment?.schemaVersion === FUTURE_ACTION_COMMITMENT_SCHEMA_VERSION
    && commitment?.type === FUTURE_ACTION_COMMITMENT_TYPE
  );
}

function normalizedPromise(commitment) {
  const promise = typeof commitment?.promise === 'string'
    ? commitment.promise.trim()
    : '';
  return promise;
}

function validScheduledMs(commitment) {
  const scheduledMs = coerceTimestampMs(commitment?.scheduledFor);
  return Number.isFinite(scheduledMs) && scheduledMs > 0 ? scheduledMs : 0;
}

export function selectFutureActionCommitmentDisplayState(commitment) {
  if (!hasCanonicalEnvelope(commitment)) {
    return { kind: 'unavailable' };
  }

  const promise = normalizedPromise(commitment);
  if (!promise) {
    return { kind: 'unavailable' };
  }

  if (commitment?.status === 'active') {
    const scheduledMs = validScheduledMs(commitment);
    if (!scheduledMs) {
      return { kind: 'unavailable' };
    }
    return { kind: 'active', promise, scheduledMs };
  }

  if (commitment?.status === 'settled') {
    const outcome = commitment?.settlement?.outcome;
    if (!SETTLED_OUTCOMES.has(outcome)) {
      return { kind: 'unavailable' };
    }
    return { kind: 'settled', promise, outcome };
  }

  return { kind: 'unavailable' };
}

export function futureActionCommitmentTitle() {
  return 'Scheduled follow-up';
}

export function futureActionCommitmentUnavailableText() {
  return 'Scheduled follow-up could not be shown.';
}

export function futureActionCommitmentPromise(displayState) {
  return displayState?.promise || '';
}

export function futureActionCommitmentScheduledForText(displayState) {
  const scheduledMs = displayState?.scheduledMs;
  if (!Number.isFinite(scheduledMs) || scheduledMs <= 0) return '';
  return new Date(scheduledMs).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function futureActionCommitmentWhisperText(displayState) {
  const promise = displayState?.promise || '';
  switch (displayState?.outcome) {
    case 'fulfilled':
      return `Done: ${promise}`;
    case 'cancelled':
      return `Cancelled: ${promise}`;
    case 'failed':
      return `Couldn't complete: ${promise}`;
    default:
      return futureActionCommitmentUnavailableText();
  }
}
